import React, { useState, useEffect } from "react"
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  useMediaQuery,
  useTheme,
} from "@mui/material"
import {
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  LocalShipping as ShippingIcon,
} from "@mui/icons-material"
import { DataGrid, GridColDef } from "@mui/x-data-grid"
import { apiService, Shipment } from "../services/apiService"

const Shipments: React.FC = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("md"))

  const [shipments, setShipments] = useState<Shipment[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedShipment, setSelectedShipment] = useState<Shipment | null>(
    null
  )
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [viewDialogOpen, setViewDialogOpen] = useState(false)
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error",
  })

  const [newShipment, setNewShipment] = useState({
    shipmentNumber: "",
    customerId: "",
    origin: { street: "", city: "", state: "", country: "", postalCode: "" },
    destination: {
      street: "",
      city: "",
      state: "",
      country: "",
      postalCode: "",
    },
    serviceLevel: "STANDARD",
    items: [
      {
        sku: "",
        description: "",
        quantity: 1,
        unitWeight: { value: 0, unit: "kg" },
        unitValue: { amount: 0, currency: "USD" },
        dimensions: { length: 0, width: 0, height: 0, unit: "cm" },
      },
    ],
    requiredDeliveryDate: "",
  })

  useEffect(() => {
    fetchShipments()
  }, [])

  const fetchShipments = async () => {
    try {
      setLoading(true)
      const data = await apiService.getShipments()
      setShipments(data.shipments)
    } catch (error) {
      console.error("Failed to fetch shipments:", error)
      setSnackbar({
        open: true,
        message: "Failed to fetch shipments",
        severity: "error",
      })
    } finally {
      setLoading(false)
    }
  }

  const handleCreateShipment = async () => {
    try {
      await apiService.createShipment(newShipment)
      setSnackbar({
        open: true,
        message: "Shipment created successfully",
        severity: "success",
      })
      setCreateDialogOpen(false)
      fetchShipments()
      resetNewShipment()
    } catch (error) {
      console.error("Failed to create shipment:", error)
      setSnackbar({
        open: true,
        message: "Failed to create shipment",
        severity: "error",
      })
    }
  }

  const resetNewShipment = () => {
    setNewShipment({
      shipmentNumber: "",
      customerId: "",
      origin: { street: "", city: "", state: "", country: "", postalCode: "" },
      destination: {
        street: "",
        city: "",
        state: "",
        country: "",
        postalCode: "",
      },
      serviceLevel: "STANDARD",
      items: [
        {
          sku: "",
          description: "",
          quantity: 1,
          unitWeight: { value: 0, unit: "kg" },
          unitValue: { amount: 0, currency: "USD" },
          dimensions: { length: 0, width: 0, height: 0, unit: "cm" },
        },
      ],
      requiredDeliveryDate: "",
    })
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case "DRAFT":
        return "default"
      case "IN_TRANSIT":
        return "primary"
      case "DELIVERED":
        return "success"
      case "CANCELLED":
        return "error"
      case "AT_FACILITY":
        return "warning"
      case "OUT_FOR_DELIVERY":
        return "info"
      default:
        return "default"
    }
  }

  const MobileShipmentCard = ({ shipment }: { shipment: Shipment }) => (
    <Card
      sx={{
        mb: 2,
        p: 2,
        background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
        border: "1px solid rgba(26, 35, 126, 0.1)",
        boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
          <ShippingIcon sx={{ mr: 1, color: "primary.main", fontSize: 20 }} />
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, fontSize: "0.875rem" }}
          >
            {shipment.shipmentNumber}
          </Typography>
        </Box>
        <Chip
          label={shipment.status.replace("_", " ")}
          color={getStatusColor(shipment.status) as any}
          size="small"
          sx={{ fontSize: "0.7rem" }}
        />
      </Box>

      <Box
        sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, mb: 2 }}
      >
        <Box>
          <Typography variant="caption" color="text.secondary">
            Customer
          </Typography>
          <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
            {shipment.customerId}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Service
          </Typography>
          <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
            {shipment.serviceLevel}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            From
          </Typography>
          <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
            {`${shipment.origin.city}, ${shipment.origin.state}`}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            To
          </Typography>
          <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
            {`${shipment.destination.city}, ${shipment.destination.state}`}
          </Typography>
        </Box>
        <Box sx={{ gridColumn: "1 / -1" }}>
          <Typography variant="caption" color="text.secondary">
            Created
          </Typography>
          <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
            {new Date(shipment.createdAt).toLocaleDateString()}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
        <IconButton
          size="small"
          onClick={() => {
            setSelectedShipment(shipment)
            setViewDialogOpen(true)
          }}
          sx={{ color: "primary.main", p: 0.5 }}
        >
          <ViewIcon sx={{ fontSize: 16 }} />
        </IconButton>
        <IconButton size="small" sx={{ color: "secondary.main", p: 0.5 }}>
          <EditIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>
    </Card>
  )

  const columns: GridColDef[] = [
    {
      field: "shipmentNumber",
      headerName: "Shipment #",
      width: 120,
      minWidth: 100,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <ShippingIcon
            sx={{ mr: 1, color: "primary.main", fontSize: { xs: 16, sm: 20 } }}
          />
          <Typography
            variant="body2"
            sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
          >
            {params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      width: 100,
      minWidth: 80,
      renderCell: (params) => (
        <Chip
          label={params.value.replace("_", " ")}
          color={getStatusColor(params.value) as any}
          size="small"
          sx={{ fontSize: { xs: "0.7rem", sm: "0.75rem" } }}
        />
      ),
    },
    {
      field: "customerId",
      headerName: "Customer",
      width: 100,
      minWidth: 80,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: "origin",
      headerName: "Origin",
      width: 120,
      minWidth: 100,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
        >
          {`${params.value.city}, ${params.value.state}`}
        </Typography>
      ),
    },
    {
      field: "destination",
      headerName: "Destination",
      width: 120,
      minWidth: 100,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
        >
          {`${params.value.city}, ${params.value.state}`}
        </Typography>
      ),
    },
    {
      field: "serviceLevel",
      headerName: "Service",
      width: 100,
      minWidth: 80,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: "createdAt",
      headerName: "Created",
      width: 100,
      minWidth: 80,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
        >
          {new Date(params.value).toLocaleDateString()}
        </Typography>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 80,
      minWidth: 70,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <IconButton
            size="small"
            onClick={() => {
              setSelectedShipment(params.row)
              setViewDialogOpen(true)
            }}
            sx={{
              color: "primary.main",
              p: { xs: 0.5, sm: 1 },
              "& .MuiSvgIcon-root": { fontSize: { xs: 16, sm: 20 } },
            }}
          >
            <ViewIcon />
          </IconButton>
          <IconButton
            size="small"
            sx={{
              color: "secondary.main",
              p: { xs: 0.5, sm: 1 },
              "& .MuiSvgIcon-root": { fontSize: { xs: 16, sm: 20 } },
            }}
          >
            <EditIcon />
          </IconButton>
        </Box>
      ),
    },
  ]

  return (
    <Box sx={{ width: "100%", overflow: "hidden" }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          mb: 4,
          gap: { xs: 2, sm: 0 },
        }}
      >
        <Box>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              background: "linear-gradient(135deg, #1a237e 0%, #534bae 100%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              mb: 1,
              fontSize: { xs: "1.5rem", sm: "2rem", md: "2.5rem" },
            }}
          >
            Shipments
          </Typography>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{
              fontWeight: 400,
              fontSize: { xs: "0.875rem", sm: "1.25rem" },
              display: { xs: "none", sm: "block" },
            }}
          >
            Manage and track your shipment operations
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
          sx={{
            background: "linear-gradient(135deg, #1a237e 0%, #534bae 100%)",
            borderRadius: 2,
            px: { xs: 2, sm: 3 },
            py: { xs: 1, sm: 1.5 },
            fontSize: { xs: "0.875rem", sm: "1rem" },
            fontWeight: 600,
            textTransform: "none",
            boxShadow: "0px 8px 20px rgba(26, 35, 126, 0.3)",
            width: { xs: "100%", sm: "auto" },
            "&:hover": {
              background: "linear-gradient(135deg, #000051 0%, #1a237e 100%)",
              boxShadow: "0px 12px 25px rgba(26, 35, 126, 0.4)",
              transform: "translateY(-2px)",
            },
          }}
        >
          Create Shipment
        </Button>
      </Box>

      {isMobile ? (
        <Box>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <Typography>Loading shipments...</Typography>
            </Box>
          ) : (
            shipments.map((shipment) => (
              <MobileShipmentCard key={shipment.id} shipment={shipment} />
            ))
          )}
        </Box>
      ) : (
        <Card
          sx={{
            background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
            border: "1px solid rgba(26, 35, 126, 0.1)",
            boxShadow:
              "0px 4px 6px rgba(0, 0, 0, 0.1), 0px 2px 4px rgba(0, 0, 0, 0.06)",
            overflow: "hidden",
          }}
        >
          <CardContent sx={{ p: 0, overflow: "auto" }}>
            <DataGrid
              rows={shipments}
              columns={columns}
              loading={loading}
              pageSizeOptions={[5, 10, 25, 50]}
              initialState={{
                pagination: { paginationModel: { pageSize: 10 } },
              }}
              disableRowSelectionOnClick
              autoHeight
              disableColumnMenu
              disableColumnFilter
              disableColumnSelector
              sx={{
                border: "none",
                "& .MuiDataGrid-cell:focus": {
                  outline: "none",
                },
                "& .MuiDataGrid-columnHeaders": {
                  background:
                    "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
                  borderBottom: "2px solid #1a237e",
                  "& .MuiDataGrid-columnHeaderTitle": {
                    fontWeight: 600,
                    fontSize: "0.875rem",
                    color: "#1a237e",
                  },
                },
                "& .MuiDataGrid-row": {
                  "&:hover": {
                    backgroundColor: "rgba(26, 35, 126, 0.04)",
                  },
                  "&.Mui-selected": {
                    backgroundColor: "rgba(26, 35, 126, 0.08)",
                    "&:hover": {
                      backgroundColor: "rgba(26, 35, 126, 0.12)",
                    },
                  },
                },
                "& .MuiDataGrid-cell": {
                  borderBottom: "1px solid rgba(0, 0, 0, 0.05)",
                  padding: "16px",
                  fontSize: "0.875rem",
                },
                "& .MuiDataGrid-footerContainer": {
                  padding: "16px",
                  fontSize: "0.875rem",
                },
                "& .MuiTablePagination-root": {
                  fontSize: "0.875rem",
                },
              }}
            />
          </CardContent>
        </Card>
      )}

      {/* Create Shipment Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Create New Shipment</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Shipment Number"
                value={newShipment.shipmentNumber}
                onChange={(e) =>
                  setNewShipment({
                    ...newShipment,
                    shipmentNumber: e.target.value,
                  })
                }
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Customer ID"
                value={newShipment.customerId}
                onChange={(e) =>
                  setNewShipment({ ...newShipment, customerId: e.target.value })
                }
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Typography variant="h6" gutterBottom>
                Origin
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Street"
                value={newShipment.origin.street}
                onChange={(e) =>
                  setNewShipment({
                    ...newShipment,
                    origin: { ...newShipment.origin, street: e.target.value },
                  })
                }
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField
                fullWidth
                label="City"
                value={newShipment.origin.city}
                onChange={(e) =>
                  setNewShipment({
                    ...newShipment,
                    origin: { ...newShipment.origin, city: e.target.value },
                  })
                }
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField
                fullWidth
                label="State"
                value={newShipment.origin.state}
                onChange={(e) =>
                  setNewShipment({
                    ...newShipment,
                    origin: { ...newShipment.origin, state: e.target.value },
                  })
                }
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Typography variant="h6" gutterBottom>
                Destination
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Street"
                value={newShipment.destination.street}
                onChange={(e) =>
                  setNewShipment({
                    ...newShipment,
                    destination: {
                      ...newShipment.destination,
                      street: e.target.value,
                    },
                  })
                }
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField
                fullWidth
                label="City"
                value={newShipment.destination.city}
                onChange={(e) =>
                  setNewShipment({
                    ...newShipment,
                    destination: {
                      ...newShipment.destination,
                      city: e.target.value,
                    },
                  })
                }
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField
                fullWidth
                label="State"
                value={newShipment.destination.state}
                onChange={(e) =>
                  setNewShipment({
                    ...newShipment,
                    destination: {
                      ...newShipment.destination,
                      state: e.target.value,
                    },
                  })
                }
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Service Level</InputLabel>
                <Select
                  value={newShipment.serviceLevel}
                  onChange={(e) =>
                    setNewShipment({
                      ...newShipment,
                      serviceLevel: e.target.value,
                    })
                  }
                >
                  <MenuItem value="STANDARD">Standard</MenuItem>
                  <MenuItem value="EXPEDITED">Expedited</MenuItem>
                  <MenuItem value="EXPRESS">Express</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Required Delivery Date"
                type="datetime-local"
                value={newShipment.requiredDeliveryDate}
                onChange={(e) =>
                  setNewShipment({
                    ...newShipment,
                    requiredDeliveryDate: e.target.value,
                  })
                }
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateShipment} variant="contained">
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Shipment Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Shipment Details</DialogTitle>
        <DialogContent>
          {selectedShipment && (
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="subtitle2" color="textSecondary">
                  Shipment Number
                </Typography>
                <Typography variant="body1">
                  {selectedShipment.shipmentNumber}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="subtitle2" color="textSecondary">
                  Status
                </Typography>
                <Chip
                  label={selectedShipment.status.replace("_", " ")}
                  color={getStatusColor(selectedShipment.status) as any}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="subtitle2" color="textSecondary">
                  Customer
                </Typography>
                <Typography variant="body1">
                  {selectedShipment.customerId}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="subtitle2" color="textSecondary">
                  Service Level
                </Typography>
                <Typography variant="body1">
                  {selectedShipment.serviceLevel}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="subtitle2" color="textSecondary">
                  Origin
                </Typography>
                <Typography variant="body1">
                  {selectedShipment.origin.city},{" "}
                  {selectedShipment.origin.state}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="subtitle2" color="textSecondary">
                  Destination
                </Typography>
                <Typography variant="body1">
                  {selectedShipment.destination.city},{" "}
                  {selectedShipment.destination.state}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="subtitle2" color="textSecondary">
                  Created
                </Typography>
                <Typography variant="body1">
                  {new Date(selectedShipment.createdAt).toLocaleString()}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Typography variant="subtitle2" color="textSecondary">
                  Last Updated
                </Typography>
                <Typography variant="body1">
                  {new Date(selectedShipment.updatedAt).toLocaleString()}
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default Shipments
